import {
  IInitialViewpoint,
  ITopicPoint,
  IViewpointBimFile,
  IViewpointCamera,
  IViewpointClippingPlane,
  IViewpointComponent,
} from 'Data/interfaces/Activities/IViewpointData';
import {
  BimBcfCamera, BimBcfClippingPlane, BimTreeNode, BimViewerHandle,
} from 'Data/interfaces/BimViewer/IBimViewerPort';
import { IViewpointEngine, IViewpointRestoreReport } from './IViewpointEngine';

/** Um modelo na cena, do jeito que o viewpoint precisa registrar. */
export interface IIfcLiteViewpointModel {
  /** Identificador do modelo na Coordly. Vira `BimFiles[].Reference`. */
  urn: string;
  name: string;
  /** Índice do modelo no motor — o mesmo que a seleção reporta. */
  modelIndex: number;
  isVisible: boolean;
}

/**
 * O estado que mora no React e o motor não conhece: quais modelos estão na cena,
 * o que está selecionado e o que está oculto. Vem por função porque é lido no
 * momento da captura, não no da montagem.
 */
export interface IIfcLiteViewpointContext {
  handle: BimViewerHandle | null;
  models: IIfcLiteViewpointModel[];
  /** Ids LOCAIS ao modelo, com o modelo de cada um em `modelIndices`. */
  selection: { expressIds: number[]; modelIndices?: number[] };
  /**
   * Ids locais ocultos. Sem modelo associado de propósito: `hide` no motor
   * esconde o id em TODOS os modelos que o contêm, então o oculto é resolvido
   * contra todos na hora de gravar.
   */
  hiddenIds: number[];
  /** Ocultos pelo olho da árvore de cada modelo, que esconde só naquele modelo. */
  hiddenByModel?: Map<number, number[]>;
  /** O que o isolamento deixa à vista, por modelo. Vazio = sem isolamento. */
  isolatedByModel?: Map<number, number[]>;
}

const ORIGINATING_SYSTEM = 'Coordly 3D (ifc-lite)';

/** Formato da ponte (camelCase) → DTO do BCF que o backend guarda (PascalCase). */
const toDtoCamera = (camera: BimBcfCamera, aspectRatio: number): IViewpointCamera => {
  const isOrthographic = typeof camera.viewToWorldScale === 'number';

  const dto: IViewpointCamera = {
    Type: isOrthographic ? 'Orthogonal' : 'Perspective',
    ViewPoint: {
      X: camera.cameraViewPoint.x,
      Y: camera.cameraViewPoint.y,
      Z: camera.cameraViewPoint.z,
    },
    Direction: {
      X: camera.cameraDirection.x,
      Y: camera.cameraDirection.y,
      Z: camera.cameraDirection.z,
    },
    UpVector: {
      X: camera.cameraUpVector.x,
      Y: camera.cameraUpVector.y,
      Z: camera.cameraUpVector.z,
    },
    AspectRatio: aspectRatio,
  };

  if (isOrthographic) {
    dto.ViewToWorldScale = camera.viewToWorldScale;
  } else {
    dto.FieldOfView = camera.fieldOfView ?? 45;
  }

  return dto;
};

/** O caminho de volta. */
const toBridgeCamera = (dto: IViewpointCamera): BimBcfCamera => {
  const camera: BimBcfCamera = {
    cameraViewPoint: { x: dto.ViewPoint.X, y: dto.ViewPoint.Y, z: dto.ViewPoint.Z },
    cameraDirection: { x: dto.Direction.X, y: dto.Direction.Y, z: dto.Direction.Z },
    cameraUpVector: { x: dto.UpVector.X, y: dto.UpVector.Y, z: dto.UpVector.Z },
  };

  // Ortográfica é decidida pelo `Type`, não pela presença do campo: BCF de fora
  // pode trazer os dois preenchidos.
  if (dto.Type === 'Orthogonal') {
    camera.viewToWorldScale = dto.ViewToWorldScale;
  } else {
    camera.fieldOfView = dto.FieldOfView ?? 45;
  }

  return camera;
};

const toDtoPlane = ({ location, direction }: BimBcfClippingPlane): IViewpointClippingPlane => ({
  Location: { X: location.x, Y: location.y, Z: location.z },
  Direction: { X: direction.x, Y: direction.y, Z: direction.z },
});

const toBridgePlane = ({ Location, Direction }: IViewpointClippingPlane): BimBcfClippingPlane => ({
  location: { x: Location.X, y: Location.Y, z: Location.Z },
  direction: { x: Direction.X, y: Direction.Y, z: Direction.Z },
});

// A raiz costuma ser o IfcProject, mas modelo de disciplina pode vir sem ele.
const findIfcProject = (nodes: BimTreeNode[]): BimTreeNode | undefined => {
  for (const node of nodes) {
    if (node.kind !== 'spatial') { continue; }
    if (node.ifcType.toUpperCase() === 'IFCPROJECT') { return node; }
    const inner = findIfcProject(node.children);
    if (inner) { return inner; }
  }
  return undefined;
};

const guidsOf = (components: IViewpointComponent[]): string[] => components
  .map((component) => component.IfcGuid)
  .filter((guid): guid is string => !!guid);

/**
 * Viewpoint sobre o motor ifc-lite.
 *
 * Duas coisas o distinguem do lado Autodesk, e as duas são vantagem:
 *
 * - **O GlobalId é nativo.** O `IfcGuid` do BCF sai direto do data model, sem a
 *   conversão de GUID comprimido para UUID nem a busca por `LcOaNodeGuid` que o
 *   restore da Autodesk precisa fazer.
 * - **A visibilidade já está em memória.** Não há travessia de árvore com uma
 *   chamada de propriedades por folha; o app já sabe o que escondeu.
 *
 * O corte atravessa a ponte já como plano do BCF. O motor tem um corte por
 * eixo, então um BCF de fora com vários planos restaura só o primeiro, e o
 * plano inclinado cai no eixo dominante.
 */
export class IfcLiteViewpointEngine implements IViewpointEngine {
  constructor(
    private readonly getContext: () => IIfcLiteViewpointContext,
    private readonly getAspectRatio: () => number,
    /** Chamado a cada restauração com todos os modelos da cena prontos. */
    private readonly onRestored?: (report: IViewpointRestoreReport) => void,
  ) {}

  isReady(): boolean {
    const { handle, models } = this.getContext();
    return !!handle && models.length > 0 && !!handle.getCamera();
  }

  async capture(): Promise<IInitialViewpoint> {
    const { handle, models, selection, hiddenIds, hiddenByModel, isolatedByModel } = this.getContext();

    if (!handle) { throw new Error('Motor 3D não está montado'); }

    const camera = handle.getCamera();
    if (!camera) { throw new Error('Motor 3D não devolveu a câmera'); }

    const snapshot = await handle.captureSnapshot();
    const clippingPlanes = handle.getClippingPlanes() ?? [];
    const selectionComponents = await this.componentsFor(handle, models, this.selectionByModel(selection));
    // Oculto da toolbar vale para todo modelo que contenha o id, espelhando o `hide`.
    const hiddenOf = (modelIndex: number) => new Set([...hiddenIds, ...(hiddenByModel?.get(modelIndex) ?? [])]);
    const isolating = !!isolatedByModel && isolatedByModel.size > 0;
    // Isolado vira "tudo oculto, menos estes" (o padrão do BCF); o que foi
    // ocultado dentro do isolamento sai da lista.
    const exceptionComponents = await this.componentsFor(
      handle,
      models,
      new Map(models.map((model) => {
        const hidden = hiddenOf(model.modelIndex);
        return [
          model.modelIndex,
          isolating
            ? (isolatedByModel.get(model.modelIndex) ?? []).filter((id) => !hidden.has(id))
            : Array.from(hidden),
        ];
      })),
    );
    const ifcProjects = await this.ifcProjectsOf(handle, models);

    return {
      Index: 0,
      SnapshotBase64: snapshot ?? undefined,
      Camera: toDtoCamera(camera, this.getAspectRatio()),
      Selection: selectionComponents.length > 0 ? selectionComponents : undefined,
      ClippingPlanes: clippingPlanes.length > 0 ? clippingPlanes.map(toDtoPlane) : undefined,
      Visibility: {
        // Sem isolamento, "esconder poucos": o app sabe exatamente o que ocultou,
        // e listar o visível num modelo grande seria um payload de megabytes.
        DefaultVisibility: !isolating,
        Exceptions: exceptionComponents,
        SpacesVisible: false,
      },
      BimFiles: models.map((model): IViewpointBimFile => ({
        // Acha o modelo quando a urn não existe mais (revisão nova) e casa o
        // viewpoint com o arquivo em outro sistema BCF.
        IfcProject: ifcProjects.get(model.modelIndex),
        Filename: model.name,
        Reference: model.urn,
        IsVisible: model.isVisible,
      })),
    };
  }

  private async ifcProjectsOf(
    handle: BimViewerHandle,
    models: IIfcLiteViewpointModel[],
  ): Promise<Map<number, string>> {
    const byModel = new Map<number, string>();

    for (const model of models) {
      const project = findIfcProject(handle.getSpatialTree(model.modelIndex));
      if (project?.expressId === undefined) { continue; }

      const [match] = await handle.getGlobalIds([project.expressId], model.modelIndex);
      if (match) { byModel.set(model.modelIndex, match.globalId); }
    }

    return byModel;
  }

  apply(viewpoint: IInitialViewpoint): void {
    const { handle, models } = this.getContext();
    if (!handle) { return; }

    if (viewpoint.Camera) {
      handle.setCamera(toBridgeCamera(viewpoint.Camera));
    }
    // Sem plano também aplica: desliga o corte que estava na tela.
    handle.setClippingPlanes((viewpoint.ClippingPlanes ?? []).map(toBridgePlane));

    // A câmera vai na hora; visibilidade e seleção esperam os GlobalIds virarem
    // ids locais, que vêm do worker do data model.
    void this.applyElements(handle, models, viewpoint);
  }

  /**
   * Só visibilidade e seleção, sem mexer na câmera: para quando o data model
   * chega depois da geometria e o usuário já pode ter navegado.
   */
  restoreElements(viewpoint: IInitialViewpoint): Promise<void> {
    const { handle, models } = this.getContext();
    return handle ? this.applyElements(handle, models, viewpoint) : Promise.resolve();
  }

  private async applyElements(
    handle: BimViewerHandle,
    models: IIfcLiteViewpointModel[],
    viewpoint: IInitialViewpoint,
  ): Promise<void> {
    try {
      const found = new Set<string>();
      await this.applyVisibility(handle, models, viewpoint, found);
      await this.applySelection(handle, models, viewpoint, found);
      this.report(handle, models, viewpoint, found);
    } catch (error) {
      console.warn('[viewpoint] falha ao restaurar visibilidade/seleção', error);
    }
  }

  /**
   * Só com o data model de todos os modelos pronto: antes dele o motor não
   * resolve GlobalId nenhum, e "não encontrado" seria falso.
   */
  private report(
    handle: BimViewerHandle,
    models: IIfcLiteViewpointModel[],
    viewpoint: IInitialViewpoint,
    found: Set<string>,
  ): void {
    if (!this.onRestored) { return; }
    if (models.some((model) => handle.getSpatialTree(model.modelIndex).length === 0)) { return; }

    const total = new Set(guidsOf([
      ...(viewpoint.Selection ?? []),
      ...(viewpoint.Visibility?.Exceptions ?? []),
    ])).size;
    this.onRestored({ viewpoint, found: found.size, total });
  }

  /** Seleção agrupada por modelo, com ids locais. */
  private selectionByModel(
    selection: IIfcLiteViewpointContext['selection'],
  ): Map<number, number[]> {
    const byModel = new Map<number, number[]>();

    selection.expressIds.forEach((expressId, i) => {
      // Sem `modelIndices` (bundle antigo) tudo cai no primeiro modelo, que é o
      // comportamento do viewer de arquivo único.
      const modelIndex = selection.modelIndices?.[i] ?? 0;
      const ids = byModel.get(modelIndex);
      if (ids) { ids.push(expressId); } else { byModel.set(modelIndex, [expressId]); }
    });

    return byModel;
  }

  /** Traduz ids locais em componentes do BCF, identificados por GlobalId. */
  private async componentsFor(
    handle: BimViewerHandle,
    models: IIfcLiteViewpointModel[],
    idsByModel: Map<number, number[]>,
  ): Promise<IViewpointComponent[]> {
    const components: IViewpointComponent[] = [];

    for (const model of models) {
      const expressIds = idsByModel.get(model.modelIndex);
      if (!expressIds || expressIds.length === 0) { continue; }

      for (const { expressId, globalId } of await handle.getGlobalIds(expressIds, model.modelIndex)) {
        components.push({
          IfcGuid: globalId,
          AuthoringToolId: globalId,
          OriginatingSystem: ORIGINATING_SYSTEM,
          ComponentDetails: {
            ModelUrn: model.urn,
            ModelName: model.name,
            DbId: String(expressId),
          },
        });
      }
    }

    return components;
  }

  private async applyVisibility(
    handle: BimViewerHandle,
    models: IIfcLiteViewpointModel[],
    viewpoint: IInitialViewpoint,
    found: Set<string>,
  ): Promise<void> {
    const visibility = viewpoint.Visibility;
    handle.showAll();

    if (!visibility) { return; }

    const exceptions = await this.localIdsFor(handle, models, visibility.Exceptions ?? [], found);
    if (exceptions.size === 0) {
      // Sem exceções, `DefaultVisibility: false` esconderia tudo e deixaria a
      // tela preta. O `showAll` acima já é o estado certo.
      return;
    }

    // Por modelo: sem o índice o motor aplica o id local em todo modelo que o
    // tenha, e esconderia (ou isolaria) o elemento de mesmo id de outra disciplina.
    // Isolar por modelo mostra a união; modelo sem exceção nenhuma fica oculto.
    exceptions.forEach((ids, modelIndex) => {
      if (visibility.DefaultVisibility) {
        handle.hide(ids, modelIndex);
      } else {
        handle.isolate(ids, modelIndex);
      }
    });
  }

  private async applySelection(
    handle: BimViewerHandle,
    models: IIfcLiteViewpointModel[],
    viewpoint: IInitialViewpoint,
    found: Set<string>,
  ): Promise<void> {
    handle.clearSelection();

    const selection = viewpoint.Selection ?? [];
    if (selection.length === 0) { return; }

    // Componente de um modelo fora da cena (a revisão anterior, achada pelo
    // IfcProject) é procurado em todos: o GlobalId sobrevive à revisão.
    const inScene = new Set(models.map((model) => model.urn));

    let additive = false;
    for (const model of models) {
      const componentsOfModel = selection.filter((component) => {
        const modelUrn = component.ComponentDetails?.ModelUrn;
        return !modelUrn || modelUrn === model.urn || !inScene.has(modelUrn);
      });
      if (componentsOfModel.length === 0) { continue; }

      for (const { globalId, expressId } of await handle.getExpressIds(guidsOf(componentsOfModel), model.modelIndex)) {
        found.add(globalId);
        handle.select(expressId, { additive, modelIndex: model.modelIndex });
        additive = true;
      }
    }
  }

  /** GlobalIds do viewpoint → ids locais, por modelo da cena. */
  private async localIdsFor(
    handle: BimViewerHandle,
    models: IIfcLiteViewpointModel[],
    components: IViewpointComponent[],
    found: Set<string>,
  ): Promise<Map<number, number[]>> {
    const globalIds = guidsOf(components);
    const byModel = new Map<number, number[]>();

    if (globalIds.length === 0) { return byModel; }

    for (const model of models) {
      const matches = await handle.getExpressIds(globalIds, model.modelIndex);
      if (matches.length === 0) { continue; }
      for (const { globalId } of matches) { found.add(globalId); }
      byModel.set(model.modelIndex, matches.map(({ expressId }) => expressId));
    }

    return byModel;
  }
}
